import React, { useState } from "react";
import { getAuditTrail } from "../../services/api";
import {
  FaPlane, FaUser, FaPhone, FaTicketAlt, FaFileInvoice,
  FaCog, FaExclamationTriangle, FaMapMarkerAlt, FaCreditCard,
  FaClipboardList, FaSearch,
} from "react-icons/fa";

// ── Category display config ─────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  Segment:    { label: "Flight",        color: "bg-blue-50 text-blue-700 border-blue-200",     icon: <FaPlane size={10} /> },
  Passenger:  { label: "Passenger",     color: "bg-purple-50 text-purple-700 border-purple-200", icon: <FaUser size={10} /> },
  Contact:    { label: "Contact",       color: "bg-green-50 text-green-700 border-green-200",   icon: <FaPhone size={10} /> },
  Address:    { label: "Address",       color: "bg-green-50 text-green-700 border-green-200",   icon: <FaMapMarkerAlt size={10} /> },
  Ticketing:  { label: "Ticketing",     color: "bg-teal-50 text-teal-700 border-teal-200",     icon: <FaTicketAlt size={10} /> },
  SSR:        { label: "Service Request", color: "bg-orange-50 text-orange-700 border-orange-200", icon: <FaExclamationTriangle size={10} /> },
  Accounting: { label: "Accounting",    color: "bg-cyan-50 text-cyan-700 border-cyan-200",     icon: <FaFileInvoice size={10} /> },
  Payment:    { label: "Payment",       color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <FaCreditCard size={10} /> },
  Remark:     { label: "Remark",        color: "bg-yellow-50 text-yellow-700 border-yellow-200", icon: <FaClipboardList size={10} /> },
  System:     { label: "System",        color: "bg-gray-50 text-gray-600 border-gray-200",     icon: <FaCog size={10} /> },
  Other:      { label: "Other",         color: "bg-gray-50 text-gray-600 border-gray-200",     icon: <FaCog size={10} /> },
};

function categoryConfig(cat: string) {
  return CATEGORY_CONFIG[cat] ?? CATEGORY_CONFIG.Other;
}

// ── Types ─────────────────────────────────────────────────────────────────────

type AuditEntry = {
  entryType: string;   // category: Segment, Ticketing, Passenger, SSR, etc.
  dateTime: string;
  agentSine: string;   // human-readable agent label
  pseudoCityCode: string;
  oldData: string;
  newData: string;     // action label: "Segment Added", "Ticket Voided", etc.
  text: string;        // human-readable multi-line description
};

type AuditTrailData = {
  success: boolean;
  pnrLocator: string;
  totalEntries: number;
  entries: AuditEntry[];
  error?: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDateTime(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

const AuditTrail: React.FC = () => {
  const [pnr, setPnr] = useState("");
  const [loading, setLoading] = useState(false);
  const [trail, setTrail] = useState<AuditTrailData | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState("ALL");

  const handleFetch = async () => {
    const locator = pnr.trim().toUpperCase();
    if (!locator) return;
    setLoading(true);
    setFetchError(null);
    setTrail(null);
    setFilterType("ALL");
    try {
      const data = await getAuditTrail(locator);
      if (!data.success) {
        setFetchError(data.error ?? "Unknown error from server.");
      } else {
        setTrail(data as AuditTrailData);
      }
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.errors?.[0]?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to fetch audit trail."
      );
    } finally {
      setLoading(false);
    }
  };

  const availableTypes = trail
    ? Array.from(new Set(trail.entries.map((e) => e.entryType))).sort()
    : [];

  const filtered =
    trail?.entries.filter(
      (e) => filterType === "ALL" || e.entryType === filterType
    ) ?? [];

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">PNR Audit Trail</h1>
        <p className="text-sm text-gray-500 mt-1">
          Complete history of all changes made to a booking.
        </p>
      </div>

      {/* Query form */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            PNR Locator
          </label>
          <input
            type="text"
            value={pnr}
            onChange={(e) => setPnr(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleFetch()}
            placeholder="e.g. SFETPQ"
            maxLength={6}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-heading w-36 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button
          onClick={handleFetch}
          disabled={loading || !pnr.trim()}
          className="bg-primary text-white font-semibold px-6 py-2 rounded-full hover:bg-blue-800 transition text-sm disabled:opacity-50 flex items-center gap-2"
        >
          <FaSearch size={12} />
          {loading ? "Loading..." : "View History"}
        </button>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">
          {fetchError}
        </div>
      )}

      {trail && (
        <>
          {/* Meta */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">PNR</span>
              <p className="font-bold text-heading font-mono text-lg">{trail.pnrLocator}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Total Changes</span>
              <p className="font-bold text-heading text-lg">{trail.totalEntries}</p>
            </div>
          </div>

          {/* Category filter pills */}
          {trail.entries.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              <button
                onClick={() => setFilterType("ALL")}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                  filterType === "ALL"
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary"
                }`}
              >
                All ({trail.entries.length})
              </button>
              {availableTypes.map((cat) => {
                const count = trail.entries.filter((e) => e.entryType === cat).length;
                const cfg = categoryConfig(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => setFilterType(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition flex items-center gap-1.5 ${
                      filterType === cat
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary"
                    }`}
                  >
                    {cfg.label} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {trail.entries.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-400 text-sm">
              No history entries found for this PNR.
            </div>
          )}

          {/* Timeline */}
          {filtered.length > 0 && (
            <div className="relative">
              {/* Vertical timeline line */}
              <div className="absolute left-[19px] top-4 bottom-4 w-px bg-gray-200" />

              <div className="flex flex-col gap-4">
                {filtered.map((entry, idx) => {
                  const cfg = categoryConfig(entry.entryType);
                  const textLines = entry.text ? entry.text.split("\n").filter(Boolean) : [];

                  return (
                    <div key={idx} className="flex gap-4 relative">
                      {/* Timeline dot with icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 bg-white z-10 ${cfg.color}`}>
                        {cfg.icon}
                      </div>

                      {/* Card */}
                      <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        {/* Header row */}
                        <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-gray-50">
                          <span className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-0.5 border ${cfg.color}`}>
                            {cfg.label}
                          </span>
                          {entry.newData && (
                            <span className="text-sm font-semibold text-gray-800">
                              {entry.newData}
                            </span>
                          )}
                          <div className="flex-1" />
                          {entry.dateTime && (
                            <span className="text-xs text-gray-400">
                              {fmtDateTime(entry.dateTime)}
                            </span>
                          )}
                        </div>

                        {/* Body — human-readable description */}
                        {textLines.length > 0 && (
                          <div className="px-4 py-3">
                            {textLines.map((line, li) => (
                              <p key={li} className="text-sm text-gray-700 leading-relaxed mb-1 last:mb-0">
                                {line}
                              </p>
                            ))}
                          </div>
                        )}

                        {/* Footer — agent info */}
                        {(entry.agentSine || entry.pseudoCityCode) && (
                          <div className="px-4 py-2 bg-gray-50 flex flex-wrap gap-4 text-xs text-gray-500">
                            {entry.agentSine && (
                              <span>
                                <span className="font-semibold text-gray-600">By:</span> {entry.agentSine}
                              </span>
                            )}
                            {entry.pseudoCityCode && (
                              <span>
                                <span className="font-semibold text-gray-600">PCC:</span> {entry.pseudoCityCode}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditTrail;
